import { Link } from "react-router-dom";
import {
    Heart,
    Truck,
    Crown,
    Cross,
    BadgeCheck,
    Sparkles,
    Gift,
    HandHeart,
    Quote,
} from "lucide-react";

export default function About() {
    return (
        <div data-testid="about-page" className="cream-textured">
            {/* ===== Page hero ===== */}
            <section className="relative py-16 md:py-24 sparkle-overlay">
                <div className="absolute inset-0 leopard-edge opacity-30 pointer-events-none" />
                <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
                    <p className="font-script text-3xl text-bb-pink-script">Our Story</p>
                    <h1 className="font-serif-display text-4xl md:text-6xl tracking-[0.1em] uppercase text-bb-text">
                        About Us
                    </h1>
                    <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                        <span className="swoosh w-14" />
                        <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                        <span className="swoosh w-14" />
                    </div>
                    <p className="mt-5 text-bb-text-soft leading-relaxed">
                        A faith-based, woman-owned boutique built on family, purpose, and the belief that every blessing deserves to be celebrated. Welcome to our little corner of Pass Christian, Mississippi.
                    </p>
                </div>
            </section>

            {/* ===== Ashley story ===== */}
            <section className="py-12 md:py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center fade-up">
                    <Sparkles size={20} className="text-bb-pink-script mx-auto animate-spin-slow" />
                    <p className="font-script text-3xl text-bb-pink-script mt-3">Hi, I'm Ashley</p>
                    <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.1em] uppercase text-bb-text mt-2">
                        The Heart Behind the Boutique
                    </h2>
                    <div className="mt-3 swoosh w-24 mx-auto" />
                    <div className="mt-7 space-y-4 text-bb-text-soft leading-relaxed max-w-2xl mx-auto text-left md:text-center">
                        <p>
                            I'm a proud mama to triplets and the heart behind{" "}
                            <span className="font-script text-bb-pink-script text-xl">
                                Beyond Blessed
                            </span>{" "}
                            Gifts & More LLC. Faith, family, and a love for thoughtful gifting are the threads woven through everything we do.
                        </p>
                        <p>
                            What started as a passion for creating and gifting has grown into a boutique built on faith, family, and purpose. I believe small businesses are built on love — and ours is built on Him first.
                        </p>
                        <p>
                            Every item is handpicked with love to inspire, encourage, and remind you that you are truly{" "}
                            <span className="font-serif-display italic">Beyond Blessed.</span>
                        </p>
                    </div>

                    {/* Pull quote */}
                    <div className="mt-10 relative max-w-2xl mx-auto px-6 py-8 border border-bb-warm-brown/30 rounded-md bg-white/50">
                        <Quote
                            size={28}
                            className="text-bb-pink-script absolute -top-4 left-1/2 -translate-x-1/2 bg-bb-cream rounded-full p-1"
                        />
                        <p className="font-serif-display italic text-lg md:text-xl text-bb-warm-brown leading-relaxed">
                            "She is clothed in strength and dignity and she laughs without fear of the future."
                        </p>
                        <p className="mt-2 text-xs tracking-[0.3em] uppercase text-bb-pink-script">
                            Proverbs 31:25
                        </p>
                    </div>
                </div>
            </section>

            {/* ===== Our Mission – three column ===== */}
            <section className="bg-white/60 py-16 md:py-20 border-y border-bb-warm-brown/15">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
                    <div className="text-center fade-up">
                        <p className="font-script text-3xl text-bb-pink-script">What We Believe</p>
                        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.12em] uppercase text-bb-text mt-2">
                            Our Mission
                        </h2>
                        <div className="mt-3 swoosh w-24 mx-auto" />
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Cross,
                                title: "Built on Faith",
                                text:
                                    "Every collection begins with prayer. We hope each piece becomes a small reminder of God's goodness in your day.",
                            },
                            {
                                icon: HandHeart,
                                title: "Made With Love",
                                text:
                                    "We hand-select every item, package every order with care, and treat every customer like family.",
                            },
                            {
                                icon: Gift,
                                title: "Meant to Bless",
                                text:
                                    "Whether you're gifting a friend or treating yourself, we want every order to feel like an encouragement.",
                            },
                        ].map((m, i) => (
                            <div
                                key={m.title}
                                data-testid={`mission-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="fade-up text-center bg-bb-cream rounded-md border border-bb-warm-brown/20 p-7 hover:-translate-y-1 transition-all duration-500 hover:shadow-lg"
                                style={{ transitionDelay: `${i * 90}ms` }}
                            >
                                <div className="w-14 h-14 mx-auto rounded-full border border-bb-rose-gold/50 flex items-center justify-center">
                                    <m.icon size={22} className="text-bb-rose-gold" />
                                </div>
                                <h3 className="mt-4 font-serif-display tracking-[0.18em] uppercase text-base text-bb-text">
                                    {m.title}
                                </h3>
                                <p className="mt-3 text-sm text-bb-text-soft leading-relaxed">
                                    {m.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Our journey timeline ===== */}
            <section className="py-16 md:py-20">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center fade-up">
                        <p className="font-script text-3xl text-bb-pink-script">A Little Backstory</p>
                        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.12em] uppercase text-bb-text mt-2">
                            Our Journey
                        </h2>
                        <div className="mt-3 swoosh w-24 mx-auto" />
                    </div>

                    <ol className="mt-12 relative border-l-2 border-bb-rose-gold/40 pl-8 space-y-10">
                        {[
                            {
                                year: "The Beginning",
                                title: "A passion for gifting",
                                text:
                                    "It started simply — a mama who loved making people feel seen with the perfect thoughtful gift.",
                            },
                            {
                                year: "Growing in Faith",
                                title: "Beyond Blessed is born",
                                text:
                                    "Lifted up by family and faith, the boutique grew into a mission to spread blessings through beautiful things.",
                            },
                            {
                                year: "Today",
                                title: "Serving our community",
                                text:
                                    "From porch pickup in Pass Christian to orders all over, every package is sent with love and a prayer.",
                            },
                            {
                                year: "What's Next",
                                title: "A full storefront launching soon",
                                text:
                                    "New arrivals, seasonal drops, and more ways to wear your blessings — keep an eye out.",
                            },
                        ].map((step, i) => (
                            <li
                                key={step.year}
                                className="fade-up"
                                style={{ transitionDelay: `${i * 90}ms` }}
                                data-testid={`journey-${i}`}
                            >
                                <span className="absolute -left-[10px] mt-1 w-5 h-5 rounded-full bg-bb-pink-script border-4 border-bb-cream" />
                                <p className="text-xs tracking-[0.25em] uppercase text-bb-pink-script">
                                    {step.year}
                                </p>
                                <h3 className="font-serif-display text-xl text-bb-text mt-1">
                                    {step.title}
                                </h3>
                                <p className="mt-2 text-bb-text-soft leading-relaxed">{step.text}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ===== Values ===== */}
            <section className="bg-bb-blush-2 py-16">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
                    <div className="text-center fade-up">
                        <p className="font-script text-3xl text-bb-pink-script">Why Choose Us</p>
                        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.12em] uppercase text-bb-text mt-2">
                            Our Values
                        </h2>
                        <div className="mt-3 swoosh w-24 mx-auto" />
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Crown, title: "Woman Owned", text: "A small business with big heart, grateful for every order." },
                            { icon: Cross, title: "Faith Based", text: "Rooted in scripture and built on the belief in blessings." },
                            { icon: Heart, title: "Made With Love", text: "Every piece is handpicked just for you." },
                            { icon: BadgeCheck, title: "Premium Quality", text: "Beautiful items you'll love & cherish for years." },
                        ].map((v, i) => (
                            <div
                                key={v.title}
                                className="bg-white border border-bb-warm-brown/20 rounded-md p-6 text-center fade-up hover:-translate-y-1 transition-transform duration-500 hover:shadow-lg"
                                style={{ transitionDelay: `${i * 80}ms` }}
                                data-testid={`value-card-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="w-14 h-14 mx-auto rounded-full border border-bb-rose-gold/40 flex items-center justify-center">
                                    <v.icon size={22} className="text-bb-rose-gold" />
                                </div>
                                <h3 className="mt-4 font-serif-display tracking-[0.18em] uppercase text-sm text-bb-text">
                                    {v.title}
                                </h3>
                                <p className="mt-2 text-sm text-bb-text-soft">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Porch pickup CTA ===== */}
            <section className="py-16 cream-textured">
                <div className="max-w-3xl mx-auto px-6 text-center fade-up">
                    <Truck size={26} className="mx-auto text-bb-rose-gold" />
                    <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.12em] uppercase text-bb-text mt-3">
                        Porch Pickup Available
                    </h2>
                    <div className="mt-3 swoosh w-24 mx-auto" />
                    <p className="mt-5 text-bb-text-soft leading-relaxed">
                        Pass Christian, Mississippi locals – choose{" "}
                        <span className="font-medium text-bb-warm-brown-2">Porch Pickup</span>{" "}
                        at checkout and we'll take care of the rest. Fast, easy, and convenient.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                        <Link to="/contact" className="btn-black btn-shine" data-testid="about-contact-cta">
                            Get In Touch
                        </Link>
                        <Link to="/shop" className="btn-outline-brown" data-testid="about-shop-cta">
                            Browse Shop
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
